import { computed, type ComputedRef, type Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import useFetch from '@/composables/useFetch.ts'
import type { UtilisateurGet } from '@/interfaces/Utilisateur.ts'
import { startAuthentication } from '@simplewebauthn/browser'
import { useToastStore } from '@/stores/toast.ts'
import { useLoadingStore } from '@/stores/loading.ts'
import promiseWithTimeout from '@/mixins/promiseWithTimeout'
import { useCacheStore } from '@/stores/cache.ts'

export const useUserStore = defineStore('user', () => {
  // Dépendances
  const api = useFetch()
  const cacheStore = useCacheStore()
  const toastStore = useToastStore()
  const loadingStore = useLoadingStore()

  // Données de l'utilisateur connecté
  const utilisateur: Ref<UtilisateurGet | null> = ref(null)

  // Statuts de l'utilisateur
  const isAuthenticated: ComputedRef<boolean> = computed(() => utilisateur.value !== null)
  const isRoot: ComputedRef<boolean> = computed(() => utilisateur.value?.role_code === 'root')
  const isAdmin: ComputedRef<boolean> = computed(
    () => isRoot.value || utilisateur.value?.role_code === 'admin',
  )

  /**
   * Récupère les informations de l'utilisateur courant.
   * Appelle l’API `auth/whoami` pour déterminer si une session est active.
   */
  async function getUtilisateur() {
    try {
      if (cacheStore.online) utilisateur.value = await api.get('auth/whoami')
    } catch {
      utilisateur.value = null
    }
    return utilisateur.value
  }

  /**
   * Réinitialise entièrement l'état local de l'utilisateur.
   * Utilisé lors d’une déconnexion ou d’une erreur critique.
   */
  function resetUtilisateur() {
    utilisateur.value = null
  }

  /**
   * Signale au navigateur qu’une Passkey locale est invalide.
   * Utilise l’API `PublicKeyCredential.signalUnknownCredential` lorsqu’elle est supportée.
   *
   * @param credentialId - L’identifiant de la clé d’accès locale à invalider.
   * @returns true si le signal a été envoyé avec succès, false sinon.
   */
  async function signalInvalidCredential(credentialId: string) {
    // Vérifie la compatibilité navigateur
    // @ts-expect-error: signalUnknownCredential n'est pas encore dans les types TypeScript
    const signalFn = PublicKeyCredential.signalUnknownCredential
    if (!signalFn) {
      console.warn(
        "[WebAuthn] L'API signalUnknownCredential n'est pas supportée par ce navigateur.",
      )
      return false
    }

    try {
      // Détermine dynamiquement le RP ID
      const rpId = import.meta.env.VITE_RELYING_PARTY_ID || window.location.hostname
      console.log(
        `[WebAuthn] Tentative de suppression. RP ID: "${rpId}", Credential: "${credentialId.substring(0, 10)}..."`,
      )

      // On encapsule l'appel dans un timeout sécurisé
      await promiseWithTimeout(
        signalFn({
          rpId,
          credentialId, // base64url encoded credential ID
        }),
        800, // timeout (ms)
        "signalUnknownCredential n'a pas répondu à temps",
      )

      console.log(`[WebAuthn] Signal envoyé au navigateur pour oublier la clé locale.`)
      return true
    } catch (error) {
      console.warn('[WebAuthn] Échec lors du signalement de la clé inconnue :', error)
      return false
    }
  }

  /**
   * Tente d'authentifier l’utilisateur via Passkey (WebAuthn).
   *
   * Étapes :
   * 1. Récupère un challenge depuis le serveur.
   * 2. Demande l’utilisation d’une clé locale via le navigateur.
   * 3. Vérifie la réponse cryptographique auprès du backend.
   * 4. Si la clé locale n’existe plus côté serveur, demande au navigateur
   *    de l’oublier automatiquement (si l’API est disponible).
   * 5. Affiche les différents messages utilisateur selon les cas.
   */
  async function logIn() {
    try {
      loadingStore.start()

      // 1. Récupération des options d'authentification
      let options
      try {
        options = await api.get('auth/login-options')
      } catch {
        toastStore.danger('Impossible de contacter le serveur.')
        return
      }

      // 2. Demande de clé locale (navigateur / authenticator)
      let credential
      try {
        credential = await startAuthentication({ optionsJSON: options })
      } catch (error) {
        // Cas où l'utilisateur annule la fenêtre WebAuthn
        if (error instanceof Error && error.name === 'NotAllowedError') {
          toastStore.danger('La connexion a été annulée.')
        } else {
          toastStore.danger("Erreur lors de l'authentification locale.")
        }
        return
      }

      // 3. Vérification de la réponse WebAuthn côté backend
      try {
        utilisateur.value = await api.post('auth/login-verification', credential, true)

        toastStore.success(
          `Bienvenue <b>${utilisateur.value?.prenom} ${utilisateur.value?.nom}</b> ☺️`,
        )

        return
      } catch (error) {
        // Erreur réseau
        // @ts-expect-error: custom network error property
        if (error?.network) {
          toastStore.danger('Erreur réseau lors de la vérification. Réessayez.')
          return
        }

        // @ts-expect-error: custom error properties
        const status = error?.status
        // @ts-expect-error: custom error properties
        const message = error?.message ?? "Erreur lors de la vérification de la clé d'accès."
        // @ts-expect-error: custom error properties
        const code = error?.code ?? null

        // Cas où la clé locale n’existe plus en base de données
        if (status === 404 && code === 'CREDENTIAL_NOT_FOUND') {
          await signalInvalidCredential(credential.id)
          toastStore.info(
            "<b>Cette clé n'est plus valide côté serveur.</b><br><br>" +
              'Le navigateur a été notifié, mais vous devrez peut-être la supprimer manuellement de votre gestionnaire de mots de passe.',
            10000,
          )
          return
        }

        toastStore.danger(message)
        return
      }
    } finally {
      loadingStore.stop()
    }
  }

  /**
   * Déconnecte l’utilisateur :
   *  - Appelle l’API de logout
   *  - Réinitialise l’état local
   */
  async function logOut() {
    await api.post('auth/logout')
    resetUtilisateur()
  }

  return {
    utilisateur,
    isAuthenticated,
    isAdmin,
    isRoot,
    getUtilisateur,
    resetUtilisateur,
    logIn,
    logOut,
  }
})
